import { create } from "zustand";

// MDRS-550 (AP-13): the bundle toast's rows live here rather than in the
// toast's content, so starting another upload, recovering another case's
// tasks or navigating away adds to the list instead of replacing it — which
// used to unmount the rows and orphan their in-flight upload/poll/save.

export type RehydratedTask = {
  taskId: string;
  fileName: string;
  fileSize?: number;
  phase: "polling" | "result";
};

/** Which case (and uploader) a new or recovered row belongs to. */
export type UploadTarget = {
  caseNumber: string;
  userId: string;
  /**
   * The case page's own label ("Examinee: … • Claim: …"), shown on the rows
   * and the add-files bar. The case number itself stays off screen.
   */
  caseLabel?: string;
};

/** Where a row is in upload → split → save, as the pop-up counts it. */
export type BundleRowStatus =
  | "ready"
  | "uploading"
  | "preparing"
  | "splitting"
  | "saving"
  | "needs-review"
  | "done"
  | "failed";

export type BundleUploadRow = UploadTarget & {
  /** Row identity — never the file name, which two uploads can share. */
  id: string;
  file: File;
  /** STP-77: rows recovered from the server mount straight into this phase. */
  initialState?: { taskId: string; phase: "polling" | "result" };
  /** Bundle-splitter task once one exists; used to de-duplicate recovery. */
  taskId?: string;
  /** Work in flight that closing the toast or the page would lose. */
  busy: boolean;
  status: BundleRowStatus;
};

type BundleUploadsState = {
  rows: BundleUploadRow[];
  /**
   * The case page on screen (its upload button is mounted), or null. The
   * picker always uploads here, so a picker left open never sends files to a
   * case the user has navigated away from.
   */
  activeTarget: UploadTarget | null;
  /**
   * The user asked for the picker ("Upload case bundle") and hasn't used it
   * yet — holds off auto-minimise. The add-files bar itself shows whenever a
   * case page is on screen (MDRS-550).
   */
  isPickerOpen: boolean;
  /**
   * Cases the user opened (true) or collapsed (false) in the pop-up. Without
   * a choice, the case on screen is open (see groupRowsByCase).
   */
  caseOpen: Record<string, boolean>;
  setActiveTarget: (target: UploadTarget) => void;
  /** Leaving a case page: only clears it if it is still the one on screen. */
  clearActiveTarget: (caseNumber: string) => void;
  openPicker: (target: UploadTarget) => void;
  addFiles: (target: UploadTarget, files: File[]) => void;
  /** Returns how many tasks were new (not already in the list). */
  addRehydrated: (target: UploadTarget, tasks: RehydratedTask[]) => number;
  setTaskId: (id: string, taskId: string) => void;
  setBusy: (id: string, busy: boolean) => void;
  setStatus: (id: string, status: BundleRowStatus) => void;
  setCaseOpen: (caseNumber: string, open: boolean) => void;
  removeRow: (id: string) => void;
  clear: () => void;
};

// Rehydrated tasks come from the backend list endpoint and don't have File
// blobs. FilePreview only reads name + size, so we build a zero-byte
// placeholder and override `size` to the persisted value via defineProperty
// (avoids allocating real bytes for large files).
const placeholderFile = (task: RehydratedTask): File => {
  const placeholder = new File([], task.fileName);
  if (task.fileSize != null && task.fileSize > 0) {
    Object.defineProperty(placeholder, "size", {
      value: task.fileSize,
      configurable: true,
    });
  }
  return placeholder;
};

// New rows for a case open its section again, so they aren't added out of
// sight under a case the user had collapsed.
const withoutChoice = (caseOpen: Record<string, boolean>, caseNumber: string) => {
  if (!(caseNumber in caseOpen)) return caseOpen;
  const { [caseNumber]: _dropped, ...rest } = caseOpen;
  return rest;
};

const updateRow = (
  rows: BundleUploadRow[],
  id: string,
  patch: Partial<BundleUploadRow>
): BundleUploadRow[] =>
  rows.map((row) => (row.id === id ? { ...row, ...patch } : row));

export const useBundleUploads = create<BundleUploadsState>((set, get) => ({
  rows: [],
  activeTarget: null,
  isPickerOpen: false,
  caseOpen: {},

  // The case page learns its label once the case loads; rows already listed
  // for that case (picked or recovered before then) take it too.
  setActiveTarget: (target) =>
    set((state) => {
      const { caseNumber, caseLabel } = target;
      const isStale = (row: BundleUploadRow) =>
        !!caseLabel && row.caseNumber === caseNumber && row.caseLabel !== caseLabel;
      if (!state.rows.some(isStale)) return { activeTarget: target };
      return {
        activeTarget: target,
        rows: state.rows.map((row) => (isStale(row) ? { ...row, caseLabel } : row)),
      };
    }),

  clearActiveTarget: (caseNumber) => {
    if (get().activeTarget?.caseNumber !== caseNumber) return;
    set({ activeTarget: null });
  },

  openPicker: (target) => set({ activeTarget: target, isPickerOpen: true }),

  addFiles: (target, files) =>
    set((state) => ({
      rows: [
        ...state.rows,
        ...files.map((file) => ({
          ...target,
          id: crypto.randomUUID(),
          file,
          busy: false,
          status: "ready" as const,
        })),
      ],
      isPickerOpen: false,
      caseOpen: withoutChoice(state.caseOpen, target.caseNumber),
    })),

  addRehydrated: (target, tasks) => {
    const known = new Set(
      get().rows.flatMap((row) => (row.taskId ? [row.taskId] : []))
    );
    const fresh = tasks.filter((task) => !known.has(task.taskId));
    if (fresh.length === 0) return 0;

    set((state) => ({
      rows: [
        ...state.rows,
        ...fresh.map((task) => ({
          ...target,
          id: crypto.randomUUID(),
          file: placeholderFile(task),
          initialState: { taskId: task.taskId, phase: task.phase },
          taskId: task.taskId,
          busy: false,
          status:
            task.phase === "result" ? ("saving" as const) : ("splitting" as const),
        })),
      ],
      caseOpen: withoutChoice(state.caseOpen, target.caseNumber),
    }));
    return fresh.length;
  },

  // A recovery list can include this row's task before the row learned its
  // id; the row that did the upload wins and the recovered copy goes.
  setTaskId: (id, taskId) => {
    const row = get().rows.find((r) => r.id === id);
    if (!row || row.taskId === taskId) return;
    set((state) => ({
      rows: updateRow(state.rows, id, { taskId }).filter(
        (row) => row.id === id || row.taskId !== taskId
      ),
    }));
  },

  // No-op when unchanged: rows re-report on every render, and a fresh rows
  // array each time would re-render the list into another report.
  setBusy: (id, busy) => {
    const row = get().rows.find((r) => r.id === id);
    if (!row || row.busy === busy) return;
    set((state) => ({ rows: updateRow(state.rows, id, { busy }) }));
  },

  // Same no-op rule as setBusy. Progress percentages stay in the row: a
  // store update per upload tick would re-render every row.
  setStatus: (id, status) => {
    const row = get().rows.find((r) => r.id === id);
    if (!row || row.status === status) return;
    set((state) => ({ rows: updateRow(state.rows, id, { status }) }));
  },

  setCaseOpen: (caseNumber, open) =>
    set((state) => ({ caseOpen: { ...state.caseOpen, [caseNumber]: open } })),

  // Removing the last row counts as asking for the picker again, so the toast
  // isn't auto-minimised out from under the user.
  removeRow: (id) =>
    set((state) => {
      const rows = state.rows.filter((row) => row.id !== id);
      return rows.length > 0 ? { rows } : { rows, isPickerOpen: true };
    }),

  clear: () => set({ rows: [], isPickerOpen: false, caseOpen: {} }),
}));

/** Case the user just asked to add files to, or null (see isPickerOpen). */
export const selectPickerTarget = (
  state: Pick<BundleUploadsState, "isPickerOpen" | "activeTarget">
): UploadTarget | null => (state.isPickerOpen ? state.activeTarget : null);

export const selectHasBusyRows = (state: Pick<BundleUploadsState, "rows">) =>
  state.rows.some((row) => row.busy);

/**
 * The header X minimises rather than closes: closing clears the list, which
 * loses work in flight and any picked file not started yet. Only the unload
 * warning is limited to work in flight — a picked file is simply re-picked.
 */
export const selectShouldMinimise = (state: Pick<BundleUploadsState, "rows">) =>
  state.rows.some((row) => row.busy || row.status === "ready");
